/**
 * The pinned dashboard's draggable, resizable board — a 12-column, fixed-row-height grid built
 * on `react-grid-layout`. `x`/`y`/`w`/`h` are the exact convention `PinRecord` documents (shared
 * with the backend); cols stay fixed at 12 so that contract never has to be reinterpreted for a
 * narrower viewport.
 *
 * Drag/resize only update local state live (`onLayoutChange` fires per pointer move); the
 * server call fires once, on `onDragStop`/`onResizeStop`, with the whole board — moving one card
 * can push others, and the backend takes the client's arrangement as authoritative.
 */

import "react-grid-layout/css/styles.css";

import { useEffect, useMemo, useRef, useState } from "react";
import { GridLayout, type Layout, useContainerWidth } from "react-grid-layout";

import { type PinRecord, useUpdatePinLayout } from "@/lib/api";
import { logger } from "@/lib/logger";

import { PinCard } from "./pin-card";

const COLS = 12;
const ROW_HEIGHT = 44;
const MARGIN: readonly [number, number] = [16, 16];

function layoutFromPins(pins: PinRecord[]): Layout {
  return pins.map((pin) => ({
    i: pin.id,
    x: pin.x ?? 0,
    y: pin.y ?? 0,
    w: pin.w ?? 4,
    h: pin.h ?? 6,
    // Floor, not the default: `createPin` sizes a new card from its template. This only
    // stops a drag from shrinking a card below the point where its chart stops being legible.
    minW: 3,
    minH: 4,
  }));
}

interface PinBoardProps {
  dashboardId: string;
  pins: PinRecord[];
  onUnpin: (pinId: string, conversationId: string | null) => void;
  unpinningId: string | null;
}

export function PinBoard({ dashboardId, pins, onUnpin, unpinningId }: PinBoardProps) {
  const { width, containerRef, mounted } = useContainerWidth();
  const updateLayoutMutation = useUpdatePinLayout();
  const [layout, setLayout] = useState<Layout>(() => layoutFromPins(pins));
  // While true, an edit is in progress (or its save is in flight) — the next `pins` refetch is
  // skipped so the save's own echo can't visibly snap the board back mid-gesture.
  const editingRef = useRef(false);

  useEffect(() => {
    if (!editingRef.current) {
      setLayout(layoutFromPins(pins));
    }
  }, [pins]);

  function persist(next: Layout) {
    setLayout(next);
    updateLayoutMutation.mutate(
      {
        dashboardId,
        items: next.map((item) => ({ id: item.i, x: item.x, y: item.y, w: item.w, h: item.h })),
      },
      {
        onSettled: () => {
          editingRef.current = false;
        },
        onError: (err) => {
          logger.warn("[dashboard] failed to save pin layout", err);
        },
      },
    );
  }

  const pinById = useMemo(() => new Map(pins.map((p) => [p.id, p])), [pins]);

  return (
    <div ref={containerRef}>
      {mounted ? (
        <GridLayout
          width={width}
          layout={layout}
          gridConfig={{ cols: COLS, rowHeight: ROW_HEIGHT, margin: MARGIN }}
          dragConfig={{ cancel: "[data-no-drag]" }}
          onLayoutChange={setLayout}
          onDragStart={() => {
            editingRef.current = true;
          }}
          onResizeStart={() => {
            editingRef.current = true;
          }}
          onDragStop={(next) => persist(next)}
          onResizeStop={(next) => persist(next)}
        >
          {layout.map((item) => {
            const pin = pinById.get(item.i);
            if (!pin) {
              return null;
            }
            return (
              <div key={item.i}>
                <PinCard
                  pin={pin}
                  onUnpin={() => onUnpin(pin.id, pin.view.conversation_id ?? null)}
                  unpinning={unpinningId === pin.id}
                />
              </div>
            );
          })}
        </GridLayout>
      ) : null}
    </div>
  );
}
